import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { uiFeedbackService, type ToastMessage } from '../../services/UIFeedbackService';
import { APP_FADE, APP_SPRING_GENTLE, modalBackdropVariants, modalPanelVariants } from '../../utils/motion';

const toneStyles: Record<ToastMessage['tone'], { icon: React.ReactNode; className: string }> = {
    info: {
        icon: <Info size={18} />,
        className: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/70 dark:text-blue-100',
    },
    success: {
        icon: <CheckCircle2 size={18} />,
        className: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/70 dark:text-emerald-100',
    },
    warning: {
        icon: <TriangleAlert size={18} />,
        className: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/70 dark:text-amber-100',
    },
    error: {
        icon: <AlertCircle size={18} />,
        className: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/70 dark:text-rose-100',
    },
};

export const FeedbackHost: React.FC = () => {
    const prefersReducedMotion = useReducedMotion();
    const { toasts, activeConfirm } = React.useSyncExternalStore(
        uiFeedbackService.subscribe,
        uiFeedbackService.getSnapshot,
        uiFeedbackService.getSnapshot,
    );

    React.useEffect(() => {
        const timers = toasts.map((toast) =>
            window.setTimeout(() => {
                uiFeedbackService.dismissToast(toast.id);
            }, toast.durationMs),
        );

        return () => {
            timers.forEach((timerId) => window.clearTimeout(timerId));
        };
    }, [toasts]);

    return (
        <>
            <div className="pointer-events-none fixed inset-x-0 top-4 z-[110] flex flex-col items-center gap-2 px-4">
                <AnimatePresence initial={false}>
                    {toasts.map((toast) => {
                        const tone = toneStyles[toast.tone];
                        return (
                            <motion.div
                                key={toast.id}
                                layout
                                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -16, scale: 0.96 }}
                                animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.96 }}
                                transition={prefersReducedMotion ? { duration: 0.14 } : APP_FADE}
                                className={`pointer-events-auto w-full max-w-md rounded-2xl border px-4 py-3 shadow-lg shadow-black/5 backdrop-blur-md ${tone.className}`}
                            >
                                <div className="flex items-start gap-2.5 text-sm font-medium">
                                    <span className="mt-0.5">{tone.icon}</span>
                                    <span>{toast.message}</span>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            <AnimatePresence>
                {activeConfirm && (
                    <motion.div
                        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 p-4 backdrop-blur-xs"
                        variants={modalBackdropVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                    >
                        <motion.div
                            variants={modalPanelVariants}
                            transition={prefersReducedMotion ? { duration: 0.14 } : APP_SPRING_GENTLE}
                            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl dark:bg-gray-900"
                        >
                            {activeConfirm.title && (
                                <h3 className="text-base font-bold text-gray-900 dark:text-white">{activeConfirm.title}</h3>
                            )}
                            <p className={`text-sm text-gray-600 dark:text-gray-300 ${activeConfirm.title ? 'mt-2' : ''}`}>
                                {activeConfirm.message}
                            </p>
                            <div className="mt-5 flex gap-2">
                                <button
                                    onClick={() => uiFeedbackService.resolveConfirm(false)}
                                    className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                                >
                                    {activeConfirm.cancelText}
                                </button>
                                <button
                                    onClick={() => uiFeedbackService.resolveConfirm(true)}
                                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition ${
                                        activeConfirm.tone === 'danger'
                                            ? 'bg-rose-600 hover:bg-rose-700'
                                            : 'bg-indigo-600 hover:bg-indigo-700'
                                    }`}
                                >
                                    {activeConfirm.confirmText}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
